// Access enforcement for the patient app (Phase 2 — LIVE). The REAL security
// boundary is firestore.rules (isAllowed = isAdmin || approved claim); this client
// state just shows the right screen (pending / enter-code / blocked) instead of an
// empty/erroring app when a signed-in user isn't approved.
//
// Flow: sign in with Google → /api/access/claim files a `pending` request → admin
// approves in the console, which issues a one-time 16-hex activation code → the
// user enters it on the gate screen → claims set → full access.
import { writable } from 'svelte/store';
import type { User } from 'firebase/auth';

export const ACCESS_ENFORCED = true;

export type AccessState = 'ok' | 'pending' | 'code_required' | 'blocked' | 'checking';
export const accessState = writable<AccessState>('checking');
// Set when a submitted code was rejected, so the gate can show why.
export const accessError = writable<string>('');

async function postClaim(user: User, code?: string): Promise<string | null> {
  try {
    const token = await user.getIdToken();
    const res = await fetch('/api/access/claim', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(code ? { code } : {}),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data && data.state === 'active') await user.getIdToken(true);   // pull in fresh claims
    return (data && data.state) || null;
  } catch { return null; }   // not deployed / offline
}

function applyServerState(state: string | null): void {
  if (state === 'pending' || state === 'none') accessState.set('pending');
  else if (state === 'code_required' || state === 'code_invalid') accessState.set('code_required');
  else if (state === 'blocked') accessState.set('blocked');
  // 'active' and null fall through — refreshAccess resolves from claims.
}

/** Resolve membership on sign-in: POST /api/access/claim. Creates/refreshes the
 *  registry state server-side and mirrors it into accessState. Once per session. */
let claimedFor: string | null = null;
export async function claimSelf(user: User | null): Promise<void> {
  if (!user || claimedFor === user.uid) return;
  claimedFor = user.uid;
  applyServerState(await postClaim(user));
}

/** Submit the admin-issued 16-hex activation code from the gate screen.
 *  Returns true when access was granted. */
export async function submitCode(user: User | null, code: string): Promise<boolean> {
  if (!user) return false;
  accessError.set('');
  const state = await postClaim(user, code);
  if (state === 'active') { await refreshAccess(user); return true; }
  applyServerState(state);
  accessError.set(state === 'code_invalid'
    ? "That code isn't valid. Check for typos, or ask your administrator for a fresh one."
    : state === null ? "Couldn't reach the server — try again in a moment." : '');
  return false;
}

/** Resolve the signed-in user's access from their custom claims (set by the
 *  access/admin Functions). Claims are authoritative for 'ok'; they can't
 *  distinguish pending vs code_required, so those come from claimSelf. */
export async function refreshAccess(user: User | null): Promise<void> {
  if (!ACCESS_ENFORCED) { accessState.set('ok'); return; }
  if (!user) { accessState.set('checking'); return; }
  try {
    const res = await user.getIdTokenResult();
    const c = res.claims as Record<string, unknown>;
    if (c.role === 'admin' || c.approved === true) accessState.set('ok');
    else if (c.blocked === true) accessState.set('blocked');
    else accessState.update((s) => (s === 'ok' || s === 'checking' ? 'pending' : s));
  } catch {
    accessState.update((s) => (s === 'ok' || s === 'checking' ? 'pending' : s));
  }
}
